import React, { useEffect, useMemo, useRef } from 'react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { type FlagId, AkFlag, useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

// NOTE: This flag will be displayed temporarily,
//   until enough time has passed that a majority
//   of issue submitted via forms have the
//   `business-form-{formId}` label.
//   Cleanup work is tracked in WOLVES-1043

export const FormsLabelFlag = () => {
	const { formatMessage } = useIntl();

	const { quickFiltersJql } = useFilteredViewContextInput({
		view: 'list',
	});

	const containsFilter = useMemo(
		() => quickFiltersJql && filtersByBusinessFormIdLabel(quickFiltersJql),
		[quickFiltersJql],
	);

	const { showFlag, dismissFlag } = useFlagsService();
	const flagId = useRef<FlagId>();

	useEffect(() => {
		if (containsFilter && flagId.current == null) {
			flagId.current = showFlag({
				render: (props) => (
					<AkFlag
						id=""
						icon={<InfoIcon label={formatMessage(messages.iconLabel)} />}
						title={formatMessage(messages.title)}
						description={formatMessage(messages.description)}
						onDismissed={() => {
							if (flagId.current) {
								dismissFlag(flagId.current);
							}
						}}
						actions={[
							{
								content: formatMessage(messages.viewAllLink),
								href: `?filter=${encodeURIComponent('labels = "business-form"')}`,
								onClick(_, analyticsEvent) {
									fireUIAnalytics(analyticsEvent, 'link clicked', 'viewAllBusinessFormsIssues');
								},
							},
						]}
						{...props}
					/>
				),
			});
		}

		return () => {
			if (flagId.current) {
				dismissFlag(flagId.current);
			}
		};
	}, [containsFilter, dismissFlag, formatMessage, showFlag]);

	return null;
};

const filtersByBusinessFormIdLabel = (jql: string) => {
	return jql
		.split(/ AND | OR /)
		.map((statement) => {
			const tokens = [' = ', ' in ', ' IN '];

			for (const token of tokens) {
				if (statement.includes(token)) {
					return statement.split(token);
				}
			}
			return [];
		})
		.reduce((acc: boolean, curr: string[]) => {
			const [field, values] = curr;
			if (field === 'labels' && values.match(/business-form-\d/)) {
				return true;
			}
			return acc;
		}, false);
};
